// src/components/ReadhitComponent/ScatterPlotComponent.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceArea, ReferenceLine, Label,
} from 'recharts';
import _ from 'lodash';
import UserGuidScatterPlot from '../../utils/UserGuid/UserGuidScatterPlot.js';

const MAX_POINTS = 50000;

const SCATTER_COLORS = {
  PV1: '#2563eb', PV2: '#16a34a', PV3: '#f59e0b',
  'VP1_PV1': '#ef4444', 'VP1_PV2': '#8b5cf6', 'VP1_PV3': '#06b6d4',
  'VP2_PV1': '#ec4899', 'VP2_PV2': '#84cc16', 'VP2_PV3': '#f97316',
  'VP3_PV1': '#14b8a6', 'VP3_PV2': '#0ea5e9', 'VP3_PV3': '#a78bfa',
  Unknown:   '#94a3b8',
};

function getColor(type) {
  if (SCATTER_COLORS[type]) return SCATTER_COLORS[type];
  let hash = 0;
  for (let i = 0; i < type.length; i++) hash = type.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360},65%,55%)`;
}

function fmtVal(v, type = '') {
  if (typeof v !== 'number') return v;
  return type === 'log10' ? v.toFixed(3) : v.toFixed(2);
}

function ScatterTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
      padding: '10px 12px', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,.1)',
    }}>
      <p style={{ margin: '0 0 5px', fontWeight: 700 }}>{d.name}</p>
      <p style={{ margin: '2px 0' }}><strong>Poly Type:</strong> {d.poltype}</p>
      <p style={{ margin: '2px 0' }}><strong>% Identity:</strong> {fmtVal(d.x, 'pident')}%</p>
      <p style={{ margin: '2px 0' }}>
        <strong>Align Len (Log10):</strong> {fmtVal(d.y, 'log10')}
        <span style={{ color: '#94a3b8', fontSize: 10 }}> (orig: {d.originalY}bp)</span>
      </p>
      {d.details?.sseqid && <p style={{ margin: '2px 0' }}><strong>Subject:</strong> {d.details.sseqid}</p>}
    </div>
  );
}

function ScatterPlotComponent({ data }) {
  const [guideOpen, setGuideOpen] = useState(false);
  const [filterVal,    setFilterVal]    = useState('all');
  const [filterOpts,   setFilterOpts]   = useState(['all']);
  const [enableFilter, setEnableFilter] = useState(true);
  const [xMin, setXMin] = useState('75');
  const [xMax, setXMax] = useState('101');
  const [yMin, setYMin] = useState('2.0');
  const [yMax, setYMax] = useState('');
  const [grouped,  setGrouped]  = useState({});
  const [warnLimit,setWarnLimit]= useState(false);
  const [error,    setError]    = useState(null);

  // ── Extract filter options once ────────────────────────────────────────────
  useEffect(() => {
    if (!data?.length) return;
    const opts = new Set(['all']);
    data.forEach(d => {
      if (d.poltype) opts.add(d.poltype);
      if (d.sseqid) {
        const p = d.sseqid.split('_');
        if (p.length >= 2) opts.add(`${p[0]}_${p[1]}`);
      }
    });
    setFilterOpts([...opts].sort());
  }, [data]);

  // ── Process scatter data ───────────────────────────────────────────────────
  useEffect(() => {
    if (!data?.length) { setGrouped({}); return; }
    setError(null);

    try {
      let src = data;
      if (src.length > MAX_POINTS) { setWarnLimit(true); src = src.slice(0, MAX_POINTS); }
      else setWarnLimit(false);

      let pts = src.map(item => {
        const x  = parseFloat(item.pident) || 0;
        const yr = parseFloat(item.len)    || 1;
        const y  = yr > 0 ? Math.log10(yr) : 0;
        const parts = (item.sseqid || '').split('_');
        const prefix = parts.length >= 2 ? `${parts[0]}_${parts[1]}` : (item.sseqid || 'Unknown');
        return {
          x, y, originalY: yr,
          poltype:  item.poltype || 'Unknown',
          name:     `${item.qseqid || ''} – ${item.sseqid || ''}`,
          details:  item,
          _prefix:  prefix,
        };
      });

      if (enableFilter) {
        if (xMin !== '') pts = pts.filter(p => p.x >= parseFloat(xMin));
        if (xMax !== '') pts = pts.filter(p => p.x <= parseFloat(xMax));
        if (yMin !== '') {
          const log = Math.log10(Math.max(0.0001, parseFloat(yMin)));
          pts = pts.filter(p => p.y >= log);
        }
        if (yMax !== '') {
          const log = Math.log10(Math.max(0.0001, parseFloat(yMax)));
          pts = pts.filter(p => p.y <= log);
        }
      }

      if (filterVal !== 'all') {
        pts = pts.filter(p => p.poltype === filterVal || p._prefix === filterVal);
      }

      const byPoltype = _.groupBy(pts, 'poltype');
      const result = Object.keys(byPoltype).length > 1
        ? byPoltype
        : _.groupBy(pts, '_prefix');

      setGrouped(result);
    } catch (e) {
      setError('Error processing data.');
      setGrouped({});
    }
  }, [data, enableFilter, xMin, xMax, yMin, yMax, filterVal]);

  const totalPts  = useMemo(() => Object.values(grouped).reduce((s, a) => s + a.length, 0), [grouped]);
  const groupKeys = Object.keys(grouped);

  const reset = () => {
    setFilterVal('all'); setEnableFilter(true);
    setXMin('75'); setXMax('101'); setYMin('2.0'); setYMax('');
  };

  return (
    <div className="rh-chart-root">

      {/* ── Controls ── */}
      <div className="rh-chart-panel">
        <div className="rh-chart-panel-title">
          Display Input
          <button className="rh-btn-reset" onClick={reset}>↺ Reset</button>
          <button className="rh-btn-reset" onClick={() => setGuideOpen(true)}>? Guide</button>
        </div>

        <div className="rh-alert rh-alert-info" style={{ fontSize: 11 }}>
          <strong>X:</strong> Percent Identity<br />
          <strong>Y:</strong> Alignment Length (Log10)
        </div>

        <div className="rh-field">
          <label className="rh-label">Filter By</label>
          <select className="rh-select" value={filterVal} onChange={e => setFilterVal(e.target.value)}>
            {filterOpts.map(v => <option key={v} value={v}>{v === 'all' ? 'All Types' : v}</option>)}
          </select>
        </div>

        <label className="rh-toggle-row">
          <span>Axis Filters</span>
          <label className="rh-switch">
            <input type="checkbox" checked={enableFilter} onChange={e => setEnableFilter(e.target.checked)} />
            <span className="rh-switch-slider" />
          </label>
        </label>

        {enableFilter && (
          <>
            <div className="rh-field">
              <label className="rh-label">% Identity Range</label>
              <div className="rh-range-row">
                <input type="number" className="rh-input" value={xMin}
                  placeholder="Min" onChange={e => setXMin(e.target.value)} />
                <input type="number" className="rh-input" value={xMax}
                  placeholder="Max" onChange={e => setXMax(e.target.value)} />
              </div>
            </div>
            <div className="rh-field">
              <label className="rh-label">Align Length Range (bp)</label>
              <div className="rh-range-row">
                <input type="number" className="rh-input" value={yMin}
                  placeholder="Min" onChange={e => setYMin(e.target.value)} />
                <input type="number" className="rh-input" value={yMax}
                  placeholder="Max" onChange={e => setYMax(e.target.value)} />
              </div>
            </div>
          </>
        )}

        {warnLimit && <div className="rh-alert rh-alert-warn">Limited to {MAX_POINTS.toLocaleString()} pts</div>}
        {error     && <div className="rh-alert rh-alert-error">{error}</div>}

        <hr />

        <div className="rh-stat-box">
          <div className="rh-stat-box-label">Total Points</div>
          <div className="rh-stat-box-value">{totalPts.toLocaleString()}</div>
        </div>
        <div className="rh-stat-box">
          <div className="rh-stat-box-label">Groups</div>
          <div className="rh-stat-box-value">{groupKeys.length}</div>
        </div>

        {groupKeys.length > 0 && (
          <div className="rh-field">
            <div className="rh-label">Legend</div>
            {groupKeys.map((k, i) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginBottom: 3 }}>
                <span className="rh-swatch" style={{ background: getColor(k), width: 12, height: 12 }} />
                <span style={{ color: 'var(--rh-text)' }}>{k}</span>
                <span style={{ color: 'var(--rh-text-faint)', marginLeft: 'auto' }}>({grouped[k]?.length ?? 0})</span>
              </div>
            ))}
          </div>
        )}

        <div className="rh-alert rh-alert-success" style={{ fontSize: 11 }}>
          Default filters improve speed with large datasets.
        </div>
      </div>

      {/* ── Chart ── */}
      <div className="rh-chart-area">
        <div className="rh-chart-area-header">
          <div className="rh-chart-area-title">% Identity vs Alignment Length (Log10)</div>
          <div className="rh-chart-area-subtitle">
            {totalPts.toLocaleString()} points{enableFilter ? ' (filtered)' : ''}
            {filterVal !== 'all' && ` — filter: ${filterVal}`}
          </div>
        </div>

        <div className="rh-chart-canvas">
          {totalPts === 0 ? (
            <div className="rh-empty"><div className="rh-empty-icon">◎</div><span>No data</span></div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 30, left: 50, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" dataKey="x" name="Percent Identity"
                  tick={{ fontSize: 11 }}
                  domain={xMin !== '' && xMax !== '' ? [parseFloat(xMin), parseFloat(xMax)] : ['auto','auto']}
                  label={{ value: 'Percent Identity (%)', position: 'insideBottom', offset: -5, fontSize: 11 }} />
                <YAxis type="number" dataKey="y" name="Alignment Length (Log10)"
                  tick={{ fontSize: 11 }} tickFormatter={v => fmtVal(v, 'log10')}
                  domain={yMin !== '' && yMax !== '' ? [Math.log10(+yMin||0.01), Math.log10(+yMax||0.01)] : ['auto','auto']}
                  label={{ value: 'Alignment Length (Log10)', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                <Tooltip content={<ScatterTooltip />} />
                <Legend verticalAlign="top" align="right" wrapperStyle={{ fontSize: 11 }} />

                {/* Reference area */}
                {enableFilter && xMin !== '' && xMax !== '' && yMin !== '' && yMax !== '' && (
                  <ReferenceArea x1={+xMin} x2={+xMax}
                    y1={Math.log10(+yMin||0.01)} y2={Math.log10(+yMax||0.01)}
                    stroke="#ef4444" strokeOpacity={0.3} fill="#ef4444" fillOpacity={0.05} />
                )}
                {enableFilter && xMin !== '' && <ReferenceLine x={+xMin} stroke="#ef4444" strokeDasharray="4 3" />}
                {enableFilter && xMax !== '' && <ReferenceLine x={+xMax} stroke="#ef4444" strokeDasharray="4 3" />}

                {groupKeys.map(k => (
                  <Scatter key={k} name={`${k} (${grouped[k]?.length ?? 0})`}
                    data={grouped[k]} fill={getColor(k)} />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <UserGuidScatterPlot open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
export default React.memo(ScatterPlotComponent);
